import fuzzysort from 'fuzzysort';
import { CartItem, CartModifierGroup } from './cart';
import { DUMMY_MOD_SYMBOL } from './constants';
import { ModSymbolCodeNameMappingType } from './mappings';
import { TopLevelMenuItem } from './menu';
import { GenericMap } from './types';
import { INFO_SLASH_COMMAND_CATEGORY } from './infoSlashCommands.options';

import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';

export function getModSymbolFromInput(
  value: string,
  modSymbolMapping: ModSymbolCodeNameMappingType
) {
  const firstWord = value.split(' ')[0];

  const modsymbols =
    firstWord === DUMMY_MOD_SYMBOL
      ? DUMMY_MOD_SYMBOL
      : Object.keys(modSymbolMapping).filter((s) => firstWord.startsWith(s));

  if (!modsymbols.length) return undefined;

  let longestMatch = modsymbols[0];

  for (const modsymbol of modsymbols) {
    if (modsymbol.length > longestMatch.length) {
      longestMatch = modsymbol;
    }
  }

  return longestMatch;
}

export const getQuantityFromInput = (
  value: string,
  modSymbolMapping: ModSymbolCodeNameMappingType
) => {
  if (!value) return { quantity: null, inputValue: '', modSymbol: null };
  const firstPartNum = parseInt(value.split(' ')[0]);
  const modSymbol = getModSymbolFromInput(value, modSymbolMapping);

  if (Number.isNaN(firstPartNum)) {
    if (modSymbol) {
      let result = {
        quantity: null,
        inputValue: value.replace(modSymbol, '')?.trim(),
        modSymbol,
      };
      return result;
    }
    return { quantity: null, inputValue: value?.trim(), modSymbol: null };
  } else {
    const inputValue = value.slice(firstPartNum.toString().length + 1)?.trim();
    return {
      quantity: firstPartNum,
      inputValue,
      modSymbol: null,
    };
  }
};

export const iterateModGroups = (
  childrenMods: GenericMap<CartModifierGroup>,
  groupsToCheck: ParsedModifierGroup[],
  menuItem: ParsedMenuItem,
  isChecked: boolean,
  inputModSymbol: string
) => {
  const children = Object.values(childrenMods || {})
    .flatMap((child) => Object.values(child.selectedItems))
    .flatMap(
      ({
        id,
        modifierGroups,
        childModifierGroups,
        modcode,
        parentModifierGroupId,
      }) => {
        if (
          id === menuItem.id &&
          parentModifierGroupId === menuItem.parentModifierGroupId &&
          (modcode === inputModSymbol || inputModSymbol === '.')
        ) {
          // Check modcode before unselecting a modifier
          isChecked = false;
        }
        isChecked = iterateModGroups(
          childModifierGroups,
          groupsToCheck,
          menuItem,
          isChecked,
          inputModSymbol
        );
        return Object.values(modifierGroups);
      }
    );
  groupsToCheck.push(...children);
  return isChecked;
};

export const getOptionLabel = (
  option: TopLevelMenuItem | string | ParsedMenuItem
) => {
  if (typeof option === 'string') {
    return option;
  }
  return option.name;
};

export const getHightlightWithQuantity = (
  quantity: number | null,
  highlight: string,
  inputValue: string
) => {
  if (
    quantity &&
    highlight.includes(quantity.toString()) &&
    !inputValue.includes(quantity.toString())
  ) {
    return `1x ${highlight}`;
  }
  return `${quantity || 1}x ${highlight}`;
};

export const getHightlight = (
  inputValue: string,
  menuItem: ParsedMenuItem | TopLevelMenuItem,
  quantity: number | null
) => {
  let highlight = menuItem.name;
  const results = fuzzysort.single(inputValue, menuItem.name);
  if (results) {
    highlight = results.highlight() || menuItem.name;
  }

  if (menuItem.synonyms) {
    let synonymResult: Fuzzysort.Result | null = null;
    let parsedSynonyms: string[];
    if (typeof menuItem.synonyms === 'string') {
      try {
        parsedSynonyms = JSON.parse(menuItem.synonyms);
      } catch {
        parsedSynonyms = [menuItem.synonyms];
      }
    } else {
      parsedSynonyms = menuItem.synonyms;
    }
    const synonyms = parsedSynonyms;

    for (const synonym of synonyms) {
      synonymResult = fuzzysort.single(inputValue, synonym);
      if (synonymResult) {
        highlight = `${highlight} [${synonymResult.highlight()}]`;
        break;
      }
    }
  }

  const isPrompt = menuItem.category === 'tts-prompt';
  const isModifier = menuItem.category === 'modifier';
  if (isPrompt) {
    highlight = `${highlight}`;
  } else if (!isModifier) {
    highlight = getHightlightWithQuantity(quantity, highlight, inputValue);
  }
  return highlight;
};

export const getFilteredOptions = (
  modSymbol: string | null,
  inputValue: string,
  quantity: number | null,
  options: (TopLevelMenuItem | ParsedMenuItem)[],
  orignalInputValue: string
): (ParsedMenuItem | TopLevelMenuItem)[] => {
  if (modSymbol === DUMMY_MOD_SYMBOL && !inputValue) {
    return options.slice(0, 50);
  }

  // HITL-1749: the generated `/info_*` answers only come up for a "/" search,
  // so they don't crowd menu items out of a plain search ("blizzard").
  const filteredOptions = orignalInputValue.startsWith('/')
    ? options.filter((option: TopLevelMenuItem | ParsedMenuItem) =>
        option.name.startsWith('/')
      )
    : options.filter(
        (option: TopLevelMenuItem | ParsedMenuItem) =>
          option.category !== INFO_SLASH_COMMAND_CATEGORY
      );

  // Normalize synonyms array to a "|"-separated string for fuzzysort search only,
  type NormalizedOption = (TopLevelMenuItem | ParsedMenuItem) & {
    _original: TopLevelMenuItem | ParsedMenuItem;
  };
  const normalizedOptions: NormalizedOption[] = filteredOptions.map(
    (option) => ({
      ...option,
      synonyms: Array.isArray(option.synonyms)
        ? option.synonyms.join('|')
        : option.synonyms || '',
      _original: option,
    })
  );

  const results = fuzzysort.go(inputValue, normalizedOptions, {
    keys: ['name', 'category', 'synonyms'],
  });

  let resultsIdObj: Record<string, boolean> = {};
  const resultObjList: (ParsedMenuItem | TopLevelMenuItem)[] = results.map(
    (v) => {
      const original = (v.obj as NormalizedOption)._original;
      resultsIdObj[original.id] = true;
      return original;
    }
  );

  // When input has quantity
  if (quantity) {
    const resultsForOrignalInput = fuzzysort.go(
      orignalInputValue,
      filteredOptions,
      {
        keys: ['name', 'category'],
      }
    );
    resultsForOrignalInput.forEach((v) => {
      if (!resultsIdObj[v.obj.id]) {
        resultObjList.unshift(v.obj);
      }
    });
  }

  return resultObjList.slice(0, 50) as (ParsedMenuItem | TopLevelMenuItem)[];
};

export const processQuantity = (
  inputQuantity: number,
  value: ParsedMenuItem | TopLevelMenuItem,
  currentMessage: string
) => {
  if (inputQuantity > 1) {
    const quantityOccurance = (
      currentMessage.match(new RegExp(inputQuantity.toString(), 'g')) || []
    ).length;
    if (
      value.name.includes(inputQuantity.toString()) &&
      quantityOccurance === 1
    ) {
      // Handle menu item with number in the name. i.e. #7 mushroom burge
      return 1;
    }
  }
  return inputQuantity;
};

export const findAnotherSelectedItem = (
  cartItems: GenericMap<CartItem>,
  selectedItemCartId: number
) => {
  const cartItemIds = Object.keys(cartItems)
    .filter((id) => id !== selectedItemCartId.toString())
    .map((id) => parseInt(id));
  return Math.max(...cartItemIds);
};
